import { ButtonHTMLAttributes, FC } from "react";
import { css } from "src/styles/theme";
import { Tooltip } from "./tooltip";
import * as VisuallyHidden from "@radix-ui/react-visually-hidden";
import cn from "classnames";

export interface FABProps extends ButtonHTMLAttributes<HTMLButtonElement> {
  look?: "primary" | "secondary";
  color?: "primary" | "danger";
  label: string;
  info?: string;
}

export const FAB: FC<FABProps> = (props) => {
  const {
    look = "secondary",
    color = "primary",
    className,
    children,
    label,
    info,
    ...other
  } = props;

  return (
    <Tooltip.Provider>
      <Tooltip.Root>
        <Tooltip.Trigger asChild>
          <button className={cn($fab({ look, color }), className)} {...other}>
            {children}
            <VisuallyHidden.Root>{label}</VisuallyHidden.Root>
          </button>
        </Tooltip.Trigger>
        <Tooltip.Content side="left" sideOffset={10}>
          {label}
          {info && <Tooltip.AdditionalInfo>{info}</Tooltip.AdditionalInfo>}
        </Tooltip.Content>
      </Tooltip.Root>
    </Tooltip.Provider>
  );
};

const $fab = css({
  applyReset: "btn",
  display: "flex",
  justifyContent: "center",
  alignItems: "center",
  borderRadius: "$full",
  textAlign: "center",
  transition: "all $duration1 ease",

  variants: {
    color: {
      primary: {
        $$textColor: "$colors$primaryText",
        $$solidColor: "$colors$primaryBgSolid",
        $$solidHoverColor: "$colors$primaryBgSolidHover",
        $$contrastColor: "$colors$onPrimary",
      },
      danger: {
        $$textColor: "$colors$dangerText",
        $$solidColor: "$colors$dangerBgSolid",
        $$solidHoverColor: "$colors$dangerBgSolidHover",
        $$contrastColor: "$colors$onDanger",
      },
    },
    look: {
      primary: {
        width: "4rem",
        height: "4rem",
        boxShadow: "$4",
        backgroundColor: "$$solidColor",
        color: "$$contrastColor",

        "&:hover": {
          backgroundColor: "$$solidHoverColor",
        },

        svg: {
          width: "2rem",
          height: "2rem",
        },
      },
      secondary: {
        width: "3rem",
        height: "3rem",
        boxShadow: "$2",
        backgroundColor: "$neutralBg",
        color: "$$textColor",

        "&:hover": {
          boxShadow: "$3",
        },

        svg: {
          width: "1.125rem",
          height: "1.125rem",
        },
      },
    },
  },
});
